<template>
    <div class="o-services-modal services-modal">
        <div class="services-modal__container">
            <div class="services-modal__label">
                <div class="services-modal__label-icon-container">
                    <img
                        class="services-modal__label-icon"
                        :src="t(obj, 'icon')"
                        :alt="`${$t('services-modal_label-icon')}`"
                    >
                </div>
                <div class="services-modal__label-info">
                    <span class="services-modal__label-text">
                        {{ t(obj, 'name') }}
                    </span>
                </div>
            </div>
            <div class="services-modal__header-block">
                <span class="services-modal__header-label">
                    {{ $t('ui.close') }}
                </span>
                <button
                    type="button"
                    class="services-modal__button"
                    :aria-label="ariaLabelCloseButton"
                    @click="onClose"
                >
                    <svg-icon
                        original
                        name="icons/close"
                        class="services-modal__button-icon"
                    />
                </button>
            </div>
            <div class="services-modal__main">
                <div class="services-modal__main-sidebar">
                    <span 
                        class="services-modal__main-sidebar-label"
                        v-html="$t('o-services-modal_main-sidebar-label', { value: t(obj, 'name') })"
                    />
                    <div 
                        ref="tilesContainer"
                        class="services-modal__tiles"
                    >
                        <button
                            v-for="(tile, index) in items"
                            :key="index"
                            class="services-modal__tile"
                            :class="{'services-modal__tile--scroll': isScroll, 'services-modal__tile--active': index === localValue}"
                            @click="onClick(index)"
                        >
                            <div class="services-modal__tile-icon-container">
                                <img 
                                    class="services-modal__tile-icon"                                        
                                    :src="t(tile, 'icon')"
                                    :alt="`${$t('services-modal_tile-icon')}`"
                                >
                            </div>
                            <span class="services-modal__tile-text">
                                {{ t(tile, 'name') }}
                            </span>
                            <div class="services-modal__tile-info-wrapper">
                                <AItemInfo 
                                    :class="['services-modal__tile-info', {'services-modal__tile-info--full-height': !t(tile, 'distance')}]"
                                    :item="getDenormalisedObjectById(tile.id)"
                                    :lastLetterNameBold="true"
                                />
                                <span 
                                    class="services-modal__tile-distance"
                                    v-if="t(tile, 'distance')"
                                >
                                    <svg-icon
                                        v-if="index === localValue"
                                        original
                                        name="icons/guide-path-white"
                                        class="services-modal__tile-distance-icon"
                                    />
                                    <svg-icon
                                        v-else
                                        original
                                        name="icons/guide-path-red"
                                        class="services-modal__tile-distance-icon"
                                    />
                                    <span class="services-modal__tile-distance-text">
                                        {{t(tile, 'distance')}} {{$t('ui.meter')}}
                                    </span>
                                </span>
                            </div>
                        </button>
                    </div>
                </div>
                <div class="services-modal__main-details">
                    <div class="services-modal__main-header">
                        <div class="services-modal__main-icon-container">
                            <img 
                                class="services-modal__main-icon"
                                :src="t(currentService, 'icon')"
                                :alt="`${$t('services-modal_main-icon')}`"
                            >
                        </div>
                        <span class="services-modal__main-headline">
                            {{ t(currentService, 'name') }}
                        </span>
                        <AItemInfo 
                            class="services-modal__main-info"
                            :item="getDenormalisedObjectById(currentService.id)"
                            :lastLetterNameBold="true"
                            :pointIcon="true"
                        />
                    </div>
                    <div class="services-modal__main-content">
                        <div class="services-modal__description" v-html="t(currentService, 'short_description')" />
                        <div 
                            v-if="t(currentService, 'distance')"
                            class="services-modal__guide-block"
                        >
                            <svg-icon
                                original
                                name="icons/guide-path-red"
                                class="services-modal__guide-icon"
                            />
                            <div class="services-modal__guide-info">
                                <span class="services-modal__guide-text">
                                    {{ $t('ui.distance') }}
                                </span>
                                <span class="services-modal__guide-distance">
                                    {{ t(currentService, 'distance') }} {{$t('ui.meter')}}
                                </span>
                            </div>
                        </div>
                    </div>
                    <ANavButton
                        :text="$t('o-services-modal_start')"
                        icon="arrow-right"
                        :to="redirectTo"
                        @onRedirect="onClose"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import sortBy from 'lodash/sortBy';

import { mapGetters } from 'vuex';

import aItemInfoWidth from '../../../utils/mixins/a-item-info-width';

import AItemInfo from '../../atoms/a-item-info/a-item-info';
import ANavButton from '../../atoms/a-nav-button/a-nav-button';

export default {
    name: 'OServicesModal',
    components: {
        AItemInfo,
        ANavButton
    },
    mixins: [aItemInfoWidth],
    props: {
        value: {
            type: Number,
            default: null
        },
        obj: {
            type: Object,
            default: () => ({})
        },
        ariaLabelCloseButton: {
            type: String,
            default: 'Close modal button'
        }
    },
    data() {
        return {
            current: 0,
            isScroll: false,
            wrapperComponent: '.services-modal__tile',
            alignedElement: '.services-modal__tile-distance'
        }
    },
    computed: {
        ...mapGetters([
            't',
            'isTerminal',
            'getCategoryById',
            'getObjectsByCategory',
            'getDenormalisedObjectById'
        ]),
        redirectTo() {
            if(this.isTerminal)
                return {
                    name: 'terminal.guide',
                    params: {
                        type: 'service',
                        id: this.currentService.id
                    }
                }
            else
                return {
                    name: 'web.guide',
                    params: {
                        type: 'services'
                    },
                    query: {
                        to: this.currentService.id
                    }
                }
        },
        items() {
            return sortBy(this.getObjectsByCategory(this.obj.id), ["distance"]);
        },
        currentService() {
            return this.items[this.current]
        },
        localValue: {
            get() {
                return this.value;
            },
            set(e) {
                this.$emit('input', e);
            }
        }
    },
    mounted() {
        this.checkScroll();
    },
    methods: {
        onClose() {
            this.$emit('onClose', true)
        },
        onClick(index) {
            this.current = index;
            this.localValue = index;
        },
        checkScroll() {
            this.isScroll = this.$refs.tilesContainer.scrollHeight > this.$refs.tilesContainer.clientHeight;
        }
    }
}
</script>

<style lang="scss">
@import "o-services-modal";
</style>
